import { useState, useEffect, useCallback } from 'react';
import { v4 as uuidv4 } from 'uuid';
import type { 
  MergeWorkspace, 
  MergedTargets, 
  MergedAspects, 
  DuplicateTarget, 
  DuplicateAspect,
  MergeOptions,
  MergeSession,
  MergeStep
} from '../types/merge';
import { productToReviewData } from '../types/evaluation';
import { apiClient } from '../api/client';

// 類似度計算（Levenshtein距離）
function levenshteinDistance(str1: string, str2: string): number {
  const matrix = Array(str2.length + 1).fill(null).map(() => Array(str1.length + 1).fill(null));

  for (let i = 0; i <= str1.length; i++) {
    matrix[0][i] = i;
  }

  for (let j = 0; j <= str2.length; j++) {
    matrix[j][0] = j;
  }

  for (let j = 1; j <= str2.length; j++) {
    for (let i = 1; i <= str1.length; i++) {
      const indicator = str1[i - 1] === str2[j - 1] ? 0 : 1;
      matrix[j][i] = Math.min(
        matrix[j][i - 1] + 1, // deletion
        matrix[j - 1][i] + 1, // insertion
        matrix[j - 1][i - 1] + indicator // substitution
      );
    }
  }

  return matrix[str2.length][str1.length];
}

function calculateSimilarity(str1: string, str2: string): number {
  const distance = levenshteinDistance(str1.toLowerCase(), str2.toLowerCase());
  const maxLength = Math.max(str1.length, str2.length);
  return maxLength === 0 ? 1 : 1 - (distance / maxLength);
}

// ローカルストレージキー
const STORAGE_KEY = 'evaloid-merge-session';
const DEFAULT_OPTIONS: MergeOptions = {
  similarityThreshold: 0.8,
  autoSave: true,
  preserveOriginals: true
};

interface UseProjectMergeOptions {
  onStepComplete?: (step: MergeStep, data: MergeWorkspace['stepData'][keyof MergeWorkspace['stepData']]) => void;
  onError?: (error: string) => void;
}

export const useProjectMerge = (options?: UseProjectMergeOptions) => {
  const [currentWorkspace, setCurrentWorkspace] = useState<MergeWorkspace | null>(null);
  const [availableWorkspaces, setAvailableWorkspaces] = useState<MergeWorkspace[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isRestoring, setIsRestoring] = useState(false);
  const [mergeOptions, setMergeOptions] = useState<MergeOptions>(DEFAULT_OPTIONS);

  // セッションをローカルストレージから復元
  const loadSession = useCallback((): MergeSession | null => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (!stored) return null;
      
      const session = JSON.parse(stored);
      // 日付文字列をDateオブジェクトに変換
      session.workspaces = session.workspaces.map((ws: MergeWorkspace) => ({
        ...ws,
        createdAt: new Date(ws.createdAt),
        updatedAt: new Date(ws.updatedAt)
      }));
      
      return session;
    } catch (error) {
      console.error('Failed to load merge session:', error);
      return null;
    }
  }, []);

  // セッションをローカルストレージに保存
  const saveSession = useCallback((session: MergeSession) => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(session));
    } catch (error) {
      console.error('Failed to save merge session:', error);
    }
  }, []);

  // 初期化時にセッションを復元
  useEffect(() => {
    setIsRestoring(true);
    const session = loadSession();
    if (session) {
      setAvailableWorkspaces(session.workspaces);
      if (session.currentWorkspaceId) {
        const currentWs = session.workspaces.find(ws => ws.id === session.currentWorkspaceId);
        if (currentWs) {
          setCurrentWorkspace(currentWs);
        }
      }
    }
    setIsRestoring(false);
  }, [loadSession]);

  // ワークスペースの更新時に自動保存
  useEffect(() => {
    if (mergeOptions.autoSave && !isRestoring) {
      const session: MergeSession = {
        workspaces: availableWorkspaces,
        currentWorkspaceId: currentWorkspace?.id,
        lastAccessed: new Date()
      };
      saveSession(session);
    }
  }, [availableWorkspaces, currentWorkspace, mergeOptions.autoSave, isRestoring, saveSession]);

  // エラー処理
  const handleError = useCallback((errorMessage: string) => {
    setError(errorMessage);
    options?.onError?.(errorMessage);
  }, [options]);

  // エラークリア
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // 新しいマージワークスペースを開始
  const startNewMerge = useCallback((projectIds: [string, string], projectTitles: [string, string]) => {
    const newWorkspace: MergeWorkspace = {
      id: uuidv4(),
      sourceProjects: projectIds,
      sourceProjectTitles: projectTitles,
      currentStep: 2, // プロジェクト選択は完了しているので、ステップ2から開始
      stepData: {},
      finalProjectTitle: `${projectTitles[0]} と ${projectTitles[1]} の統合`,
      createdAt: new Date(),
      updatedAt: new Date()
    };

    setCurrentWorkspace(newWorkspace);
    setAvailableWorkspaces(prev => [...prev, newWorkspace]);
    return newWorkspace;
  }, []);

  // ワークスペースを読み込み
  const loadWorkspace = useCallback((workspaceId: string): MergeWorkspace | null => {
    const workspace = availableWorkspaces.find(ws => ws.id === workspaceId);
    if (workspace) {
      setCurrentWorkspace(workspace);
      return workspace;
    }
    return null;
  }, [availableWorkspaces]);

  // ワークスペースを削除
  const deleteWorkspace = useCallback((workspaceId: string) => {
    setAvailableWorkspaces(prev => prev.filter(ws => ws.id !== workspaceId));
    if (currentWorkspace?.id === workspaceId) {
      setCurrentWorkspace(null);
    }
  }, [currentWorkspace]);

  // ステップデータを更新
  const updateStepData = useCallback((step: MergeStep, data: MergeWorkspace['stepData'][keyof MergeWorkspace['stepData']]) => {
    if (!currentWorkspace) return;

    const updatedWorkspace: MergeWorkspace = {
      ...currentWorkspace,
      currentStep: step,
      stepData: {
        ...currentWorkspace.stepData,
        [getStepDataKey(step)]: data
      },
      updatedAt: new Date()
    };

    setCurrentWorkspace(updatedWorkspace);
    setAvailableWorkspaces(prev => 
      prev.map(ws => ws.id === updatedWorkspace.id ? updatedWorkspace : ws)
    );

    options?.onStepComplete?.(step, data);
  }, [currentWorkspace, options]);

  // ステップデータキーを取得
  const getStepDataKey = (step: MergeStep): string => {
    switch (step) {
      case 2: return 'targets';
      case 3: return 'aspects';
      case 4: return 'rubric';
      case 5: return 'evaluations';
      case 6: return 'grades';
      default: return '';
    }
  };

  // 評価対象のマージ
  const mergeTargets = useCallback(async (projectIds: [string, string]): Promise<MergedTargets | null> => {
    setIsLoading(true);
    setError(null);

    try {
      // プロジェクトデータを取得（実際のAPIコールに置き換える）
      const [project1Data, project2Data] = await Promise.all([
        apiClient.getProjectData(projectIds[0]),
        apiClient.getProjectData(projectIds[1])
      ]);

      const products1 = project1Data.products || [];
      const products2 = project2Data.products || [];

      // 重複検出
      const duplicates: DuplicateTarget[] = [];
      
      for (let i = 0; i < products1.length; i++) {
        for (let j = 0; j < products2.length; j++) {
          const similarity = calculateSimilarity(
            products1[i].reviewTitle,
            products2[j].reviewTitle
          );
          
          if (similarity >= mergeOptions.similarityThreshold) {
            duplicates.push({
              indices: [i, j],
              similarity,
              action: 'keep_first' // デフォルト選択
            });
          }
        }
      }

      // 初期マージ結果（重複は最初のプロジェクトを優先）
      const merged = [
        ...products1,
        ...products2.filter((_, index) => 
          !duplicates.some(dup => dup.indices[1] === index && dup.action === 'keep_first')
        )
      ];

      const result: MergedTargets = {
        originalProjects: {
          [projectIds[0]]: products1,
          [projectIds[1]]: products2
        },
        duplicates,
        merged,
        userEdited: false
      };

      return result;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : '評価対象の統合に失敗しました';
      handleError(errorMessage);
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [mergeOptions.similarityThreshold, handleError]);

  // 観点のマージ
  const mergeAspects = useCallback(async (projectIds: [string, string]): Promise<MergedAspects | null> => {
    setIsLoading(true);
    setError(null);

    try {
      // プロジェクトデータを取得
      const [project1Data, project2Data] = await Promise.all([
        apiClient.getProjectData(projectIds[0]),
        apiClient.getProjectData(projectIds[1])
      ]);

      const criteria1 = project1Data.criteria || [];
      const criteria2 = project2Data.criteria || [];

      // 重複検出（タイトル + 説明文で計算）
      const duplicates: DuplicateAspect[] = [];
      
      for (let i = 0; i < criteria1.length; i++) {
        for (let j = 0; j < criteria2.length; j++) {
          const text1 = `${criteria1[i].title} ${criteria1[i].description.join(' ')}`;
          const text2 = `${criteria2[j].title} ${criteria2[j].description.join(' ')}`;
          
          const similarity = calculateSimilarity(text1, text2);
          
          if (similarity >= mergeOptions.similarityThreshold) {
            duplicates.push({
              indices: [i, j],
              similarity,
              action: 'merge' // デフォルト選択
            });
          }
        }
      }

      // 初期マージ結果
      const merged = [...criteria1];
      
      criteria2.forEach((criterion2, index2) => {
        let shouldAdd = true;
        
        duplicates.forEach(duplicate => {
          if (duplicate.indices[1] === index2) {
            switch (duplicate.action) {
              case 'merge':
                // タイトルは最初のものを使用し、説明を結合
                const mergedIndex = duplicate.indices[0];
                merged[mergedIndex] = {
                  title: criteria1[duplicate.indices[0]].title,
                  description: [
                    ...criteria1[duplicate.indices[0]].description,
                    ...criterion2.description.filter((desc: string) => 
                      !criteria1[duplicate.indices[0]].description.some((d: string) => 
                        d.toLowerCase().trim() === desc.toLowerCase().trim()
                      )
                    )
                  ]
                };
                shouldAdd = false;
                break;
              case 'keep_first':
                shouldAdd = false;
                break;
              case 'keep_second':
                merged[duplicate.indices[0]] = criterion2;
                shouldAdd = false;
                break;
              case 'keep_both':
                // 両方残すので何もしない
                break;
            }
          }
        });
        
        if (shouldAdd) {
          merged.push(criterion2);
        }
      });

      const result: MergedAspects = {
        originalProjects: {
          [projectIds[0]]: criteria1,
          [projectIds[1]]: criteria2
        },
        duplicates,
        merged,
        userEdited: false
      };

      return result;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : '観点の統合に失敗しました';
      handleError(errorMessage);
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [mergeOptions.similarityThreshold, handleError]);

  // プロジェクト最終化（新しいプロジェクトとして保存）
  const finalizeProject = useCallback(async (finalTitle: string): Promise<boolean> => {
    if (!currentWorkspace) return false;

    setIsLoading(true);
    setError(null);

    try {
      // 統合データを新しいプロジェクトとして保存
      const projectData = {
        title: finalTitle,
        products: (currentWorkspace.stepData.targets?.merged || []),
        criteria: currentWorkspace.stepData.aspects?.merged || [],
        // 他のデータも含める
      };

      await apiClient.createProject(projectData);

      // 完了したワークスペースを削除
      deleteWorkspace(currentWorkspace.id);

      return true;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'プロジェクトの保存に失敗しました';
      handleError(errorMessage);
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [currentWorkspace, deleteWorkspace, handleError]);

  return {
    // 状態
    currentWorkspace,
    availableWorkspaces,
    isLoading,
    error,
    isRestoring,
    mergeOptions,

    // アクション
    startNewMerge,
    loadWorkspace,
    deleteWorkspace,
    updateStepData,
    clearError,
    setMergeOptions,

    // マージ機能
    mergeTargets,
    mergeAspects,
    finalizeProject
  };
};